import { useEffect, useState, useCallback } from "react";
import { FaSave, FaSpinner, FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";

import "./UserPermissionsPanel.css";

const API_URL = process.env.REACT_APP_API_URL;

const ACTIONS = ["view", "add", "edit", "delete"];

/**
 * Inline panel (not a modal - same "replace the list" pattern as the
 * rest of this app) showing one user's menu permissions: their
 * role's defaults, and an optional personal override per menu.
 *
 * Each row has a "Use role default" checkbox. Checked = inherit
 * (action checkboxes disabled, greyed to show what the role default
 * currently is). Unchecked = this user gets their own custom
 * view/add/edit/delete for that menu, independent of their role.
 */
function UserPermissionsPanel({ userId, username, onClose }) {

    const [menus, setMenus] = useState([]);

    const [isLoading, setIsLoading] = useState(true);

    const [isSaving, setIsSaving] = useState(false);

    // rows[menu_key] = { useDefault: bool, view, add, edit, delete }
    const [rows, setRows] = useState({});

    const load = useCallback(async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/permissions/users/${userId}`);

            const data = await res.json();

            if (!res.ok || !data.status) {

                toast.error(data.detail || data.message || "Failed to load permissions for this account.");

                setMenus([]);

                return;

            }

            setMenus(data.menus);

            const nextRows = {};

            data.menus.forEach(menu => {

                const source = menu.has_override ? menu.override : menu.role_default;

                nextRows[menu.menu_key] = {

                    useDefault: !menu.has_override,

                    view: source.view,

                    add: source.add,

                    edit: source.edit,

                    delete: source.delete

                };

            });

            setRows(nextRows);

        } catch (err) {

            console.error("Failed to load user permissions:", err);

            toast.error("Unable to reach the server.");

        } finally {

            setIsLoading(false);

        }

    }, [userId]);

    useEffect(() => {

        load();

    }, [load]);

    const toggleUseDefault = (menuKey, menu) => {

        setRows(prev => {

            const wasUsingDefault = prev[menuKey].useDefault;

            // Flipping OFF "use default" - seed the custom checkboxes
            // with the current role-default values as a starting point,
            // rather than all-unchecked, so turning on "custom" doesn't
            // silently revoke everything.
            const seed = wasUsingDefault ? menu.role_default : prev[menuKey];

            return {

                ...prev,

                [menuKey]: {

                    ...seed,

                    useDefault: !wasUsingDefault

                }

            };

        });

    };

    const toggleAction = (menuKey, action) => {

        setRows(prev => ({

            ...prev,

            [menuKey]: {

                ...prev[menuKey],

                [action]: !prev[menuKey][action]

            }

        }));

    };

    const handleSave = async () => {

        setIsSaving(true);

        try {

            const payload = {

                rows: menus.map(menu => {

                    const row = rows[menu.menu_key];

                    return {

                        menu_key: menu.menu_key,

                        can_view: row.view,

                        can_add: row.add,

                        can_edit: row.edit,

                        can_delete: row.delete,

                        use_role_default: row.useDefault

                    };

                })

            };

            const res = await fetch(`${API_URL}/portal/permissions/users/${userId}`, {

                method: "PUT",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify(payload)

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success("Permissions updated for this account.");

                onClose();

            } else {

                toast.error(data.message || data.detail || "Failed to save permissions.");

            }

        } catch (err) {

            console.error("Failed to save user permission overrides:", err);

            toast.error("Something went wrong while saving.");

        } finally {

            setIsSaving(false);

        }

    };

    return (

        <div className="inline-form-container">

            <div className="inline-form-header">

                <button type="button" className="back-btn" onClick={onClose} disabled={isSaving}>
                    <FaArrowLeft />
                    Back to list
                </button>

                <h3>Permissions for {username}</h3>

            </div>

            {

                isLoading ? (

                    <p className="empty-note"><FaSpinner className="spin" /> Loading...</p>

                ) : (

                    <>

                        <div className="matrix-table-wrapper">

                            <table className="matrix-table">

                                <thead>

                                    <tr>
                                        <th className="menu-col">Menu</th>
                                        <th>Use role default</th>
                                        {ACTIONS.map(a => <th key={a}>{a[0].toUpperCase() + a.slice(1)}</th>)}
                                    </tr>

                                </thead>

                                <tbody>

                                    {menus.map(menu => {

                                        const row = rows[menu.menu_key] || { useDefault: true, view: false, add: false, edit: false, delete: false };

                                        return (

                                            <tr key={menu.menu_key}>

                                                <td className="menu-col">{menu.menu_label}</td>

                                                <td className="checkbox-col">
                                                    <input
                                                        type="checkbox"
                                                        checked={row.useDefault}
                                                        onChange={() => toggleUseDefault(menu.menu_key, menu)}
                                                    />
                                                </td>

                                                {ACTIONS.map(action => (

                                                    <td key={action} className="checkbox-col">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!row[action]}
                                                            disabled={row.useDefault}
                                                            onChange={() => toggleAction(menu.menu_key, action)}
                                                        />
                                                    </td>

                                                ))}

                                            </tr>

                                        );

                                    })}

                                </tbody>

                            </table>

                        </div>

                        <div className="inline-form-actions">

                            <button type="button" className="cancel-btn" onClick={onClose} disabled={isSaving}>
                                Cancel
                            </button>

                            <button type="button" className="save-btn" onClick={handleSave} disabled={isSaving}>
                                {isSaving ? <><FaSpinner className="spin" /> Saving...</> : <><FaSave /> Save Permissions</>}
                            </button>

                        </div>

                    </>

                )

            }

        </div>

    );

}

export default UserPermissionsPanel;
